import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Mail } from 'lucide-react';
import type { AppRole } from '@/lib/auth/types';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: AppRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, role, loading, bootstrapError, signOut, user } = useAuthContext();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const isSurgeonBetaDemo =
    location.pathname === '/surgeon' &&
    allowedRoles?.includes('surgeon') &&
    (query.get('demo') === 'true' ||
      query.get('beta') === 'true' ||
      localStorage.getItem('link_demo_mode') === 'true');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <div className="text-muted-foreground">Loading your account...</div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && bootstrapError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle className="text-xl">Account Setup Issue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-muted-foreground">
              {bootstrapError}
            </p>
            {user?.email && (
              <p className="text-xs text-center text-muted-foreground">
                Account: <span className="font-medium">{user.email}</span>
              </p>
            )}
            <div className="flex flex-col gap-2">
              <Button onClick={() => window.location.reload()} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button variant="outline" onClick={() => signOut()} className="w-full">
                Sign Out
              </Button>
            </div>
            <p className="text-xs text-center text-muted-foreground">
              If this persists, email{' '}
              <a href="mailto:support@linkmedicalai.com" className="underline">
                support@linkmedicalai.com
              </a>{' '}
              with your account email above.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSurgeonBetaDemo) {
    return <>{children}</>;
  }

  if (!isAuthenticated) {
    return <Navigate to={`/auth?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`} replace />;
  }

  if (!role) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Mail className="h-12 w-12 text-yellow-500" />
            </div>
            <CardTitle className="text-xl">Account Being Configured</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-muted-foreground">
              Your account is still being set up. This usually takes less than a minute.
            </p>
            {user?.email && (
              <p className="text-xs text-center text-muted-foreground">
                Account: <span className="font-medium">{user.email}</span>
              </p>
            )}
            <div className="flex flex-col gap-2">
              <Button onClick={() => window.location.reload()} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
              <Button variant="outline" onClick={() => signOut()} className="w-full">
                Sign Out
              </Button>
            </div>
            <p className="text-xs text-center text-muted-foreground">
              Still stuck? Contact your LINK administrator or email{' '}
              <a href="mailto:support@linkmedicalai.com" className="underline">
                support@linkmedicalai.com
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    if (role === 'surgeon') {
      return <Navigate to="/surgeon" replace />;
    }
    return <Navigate to="/ops" replace />;
  }

  return <>{children}</>;
}
